import $ from 'jquery';
import anime from 'animejs';
const iosInnerHeight = require('ios-inner-height');
const imagesLoaded = require('imagesloaded');
const charming = require('charming');

/**
 * Custom element for the Home Hero block.
 */
class BlockHomeHero extends window.HTMLDivElement {
  /**
   * Constructor for the BlockHomeHero element.
   * @param {HTMLElement} self - The element instance.
   * @returns {HTMLElement} The custom element.
   */
  constructor() {
    super();
    this.$ = $(this);
    this.resolveElements();
    this.initText();
    this.initEvents();
    this.initHero = this.initHero.bind(this);
    this.wrapCharacters = this.wrapCharacters.bind(this);
    this.animateCharacters = this.animateCharacters.bind(this);
    this.isHovering = false;
    imagesLoaded(this.homeHero, () => {
      this.initHero();
    });

    // Fix alignment issue due to the iOS bottom menu.
    if (this.windowHeight !== this.iosWindowHeight) {
      this.homeHeroHeader.css({ "padding-bottom": this.iosBarHeight });
    }
  }

  /**
   * Resolves necessary DOM elements.
   */
  resolveElements() {
    this.windowHeight = $(window).height();
    this.iosWindowHeight = iosInnerHeight();
    this.iosBarHeight = Math.abs(this.windowHeight - this.iosWindowHeight) + "px";
    this.wordArray = [];
    this.wordIndex = 1;
    this.homeHero = $('.home-hero');
    this.homeHeroHeader = $('.home-hero__header');
    this.$transitionalWords = $('.home-hero__transitional-text');
    this.transitionalText = document.querySelectorAll('.home-hero__transitional-text-anim');
    this.$transitionalText = $('.home-hero__transitional-text-anim');
    this.video = document.querySelector('.home-hero__video');
  }

  /**
   * Initializes event listeners.
   */
  initEvents() {
    if (this.video) {
      this.video.addEventListener("canplaythrough", this.initVideo.bind(this));
    }
  }

  /**
   * Initializes the video element and custom controls.
   */
  initVideo() {
    if (!this.video) return;

    this.video.muted = true;
    this.video.play();
    const homeHeroImage = document.querySelector('.home-hero__image.home-hero__image--video');

    // Ensure we only have one video-controls element.
    const existingControls = homeHeroImage.querySelector('.video-controls');
    if (existingControls) {
      existingControls.remove();
    }

    // Create custom play/pause button with accessibility attributes.
    const videoControls = document.createElement('div');
    videoControls.className = 'video-controls';

    const playPauseBtn = document.createElement('button');
    playPauseBtn.id = 'play-pause-btn';
    playPauseBtn.classList.add('play-pause-btn');
    playPauseBtn.setAttribute('aria-label', this.video.paused ? 'Play video' : 'Pause video');
    playPauseBtn.innerHTML = this.updateButtonIcon(false);
    videoControls.appendChild(playPauseBtn);

    this.homeHero[0].appendChild(videoControls);

    // Update hover flag and icon on mouseover.
    playPauseBtn.addEventListener('mouseover', () => {
      this.isHovering = true;
      playPauseBtn.innerHTML = this.updateButtonIcon(true);
      playPauseBtn.setAttribute('aria-label', this.video.paused ? 'Play video' : 'Pause video');
    });

    // Reset hover flag and icon on mouseout.
    playPauseBtn.addEventListener('mouseout', () => {
      this.isHovering = false;
      playPauseBtn.innerHTML = this.updateButtonIcon(false);
      playPauseBtn.setAttribute('aria-label', this.video.paused ? 'Play video' : 'Pause video');
    });

    // On click, animate and toggle video state.
    playPauseBtn.addEventListener('click', () => {
      anime({
        targets: playPauseBtn.querySelector('svg'),
        opacity: [1, 0.2, 1],
        scale: [1, 1.2, 1],
        duration: 400,
        easing: 'easeInOutQuad',
        complete: () => {
          // Update icon based on current hover state.
          playPauseBtn.innerHTML = this.updateButtonIcon(this.isHovering);
          playPauseBtn.setAttribute('aria-label', this.video.paused ? 'Play video' : 'Pause video');
        }
      });

      // Toggle video playback.
      if (this.video.paused) {
        this.video.play();
      } else {
        this.video.pause();
      }
    });
  }

  /**
   * Returns the correct SVG icon based on the video state and hover status.
   * @param {boolean} isHovered - Whether the button is hovered.
   * @returns {string} SVG markup.
   */
  updateButtonIcon(isHovered) {
    if (this.video.paused) {
      return isHovered ? this.getPlayHoverIcon() : this.getPlayIcon();
    } else {
      return isHovered ? this.getPauseHoverIcon() : this.getPauseIcon();
    }
  }

  /**
   * Returns the SVG string for the play icon.
   * @returns {string} SVG markup.
   */
  getPlayIcon() {
    return `<svg width="48" height="49" viewBox="0 0 48 49" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24.5" r="22" fill="#F4F4F2"/>
              <path d="M18.2857 16.5V33.6429L34.2857 25.0712L18.2857 16.5Z" fill="#C8102E"/>
            </svg>`;
  }

  /**
   * Returns the SVG string for the pause icon.
   * @returns {string} SVG markup.
   */
  getPauseIcon() {
    return `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="22" fill="#F4F4F2"/>
              <path d="M18.2857 33.1429V16M29.7143 33.1429V16" stroke="#C8102E" stroke-width="2.28571" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`;
  }

  /**
   * Returns the SVG string for the play hover icon.
   * @returns {string} SVG markup.
   */
  getPlayHoverIcon() {
    return `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="22" fill="#C8102E"/>
              <path d="M18.2857 16V33.1429L34.2857 24.5712L18.2857 16Z" fill="#F4F4F2"/>
            </svg>`;
  }

  /**
   * Returns the SVG string for the pause hover icon.
   * @returns {string} SVG markup.
   */
  getPauseHoverIcon() {
    return `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="22" fill="#C8102E"/>
              <path d="M18.2857 33.1429V16M29.7143 33.1429V16" stroke="#F4F4F2" stroke-width="2.28571" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`;
  }

  /**
   * Initializes the text transition animation.
   */
  initText() {
    let that = this;
    this.$transitionalWords.each(function () {
      that.wordArray.push($(this).text());
    });
    that.$transitionalText.text(that.wordArray[0]);
  }

  /**
   * Initializes the hero block.
   */
  initHero() {
    this.homeHero.addClass('home-hero--loaded');
    this.wrapCharacters();
  }

  /**
   * Wraps characters for animation.
   */
  wrapCharacters() {
    [].forEach.call(this.transitionalText, function (text) {
      charming(text);
    });
    this.animateCharacters();
  }

  /**
   * Animates the transitional text characters.
   */
  animateCharacters() {
    let that = this;
    anime.timeline()
      .add({
        offset: '+=500',
        duration: 1000,
        delay: (el, i) => i * 50,
        easing: 'easeOutCirc',
        opacity: [0, 1],
        targets: '.home-hero__transitional-text-anim span',
        translateX: (el, index) => [50 + index * 10, 0]
      })
      .add({
        offset: '+=1000',
        targets: '.home-hero__transitional-text-anim span',
        opacity: 0,
        duration: 500,
        easing: 'easeOutExpo',
        delay: (el, i) => i * 20,
        translateX: (el, index) => [-20 - index * 10],
        complete: function () {
          that.$transitionalText.text(that.wordArray[that.wordIndex]);
          that.wordIndex = ++that.wordIndex >= that.wordArray.length ? 0 : that.wordIndex;
          that.wrapCharacters();
        }
      });
  }
}
window.customElements.define('flynt-block-home-hero', BlockHomeHero, { extends: 'div' });



// WEBPACK FOOTER //
// ./Components/BlockHomeHero/script.js